import { defineComponent } from 'vue'
import { mapActions, mapState } from 'vuex'
import {
  OwnerDetailActions,
  OwnerDetailModuleActions,
  OwnerDetailModuleState
} from '~/store/owner/detail'
import BillingBackend from '~/types/billingBackend'
import { BillingInfo, GetBillingInfoPayload, SubscriptionStatusChoice } from '~/types/types'
import ContextMixin from './contextMixin'
import OwnerDetailMixin from './ownerDetailMixin'

export interface OwnerDetailStoreActions {
  fetchBillingDetails: (
    args: Parameters<OwnerDetailModuleActions[OwnerDetailActions.FETCH_BILLING_DETAILS]>[1]
  ) => ReturnType<OwnerDetailModuleActions[OwnerDetailActions.FETCH_BILLING_DETAILS]>
  fetchSeatsInfo: (
    args: Parameters<OwnerDetailModuleActions[OwnerDetailActions.FETCH_SEATS_INFO]>[1]
  ) => ReturnType<OwnerDetailModuleActions[OwnerDetailActions.FETCH_SEATS_INFO]>
  fetchBillingStatus: (
    args: Parameters<OwnerDetailModuleActions[OwnerDetailActions.FETCH_BILLING_STATUS]>[1]
  ) => ReturnType<OwnerDetailModuleActions[OwnerDetailActions.FETCH_BILLING_STATUS]>
  applyCredits: (
    args: Parameters<OwnerDetailModuleActions[OwnerDetailActions.APPLY_CREDITS]>[1]
  ) => ReturnType<OwnerDetailModuleActions[OwnerDetailActions.APPLY_CREDITS]>
  updateBillingInfo: (
    args: Parameters<OwnerDetailModuleActions[OwnerDetailActions.UPDATE_BILLING_INFO]>[1]
  ) => ReturnType<OwnerDetailModuleActions[OwnerDetailActions.UPDATE_BILLING_INFO]>
  getBillingInfo: (
    args: Parameters<OwnerDetailModuleActions[OwnerDetailActions.GET_BILLING_INFO]>[1]
  ) => ReturnType<OwnerDetailModuleActions[OwnerDetailActions.GET_BILLING_INFO]>
  subscriptionCheckout: (
    args: Parameters<OwnerDetailModuleActions[OwnerDetailActions.CHECKOUT]>[1]
  ) => ReturnType<OwnerDetailModuleActions[OwnerDetailActions.CHECKOUT]>
  updateSeats: (
    args: Parameters<OwnerDetailModuleActions[OwnerDetailActions.UPDATE_SEATS]>[1]
  ) => ReturnType<OwnerDetailModuleActions[OwnerDetailActions.UPDATE_SEATS]>
  changeSubscriptionPlan: (
    args: Parameters<OwnerDetailModuleActions[OwnerDetailActions.CHANGE_SUBSCRIPTION_PLAN]>[1]
  ) => ReturnType<OwnerDetailModuleActions[OwnerDetailActions.CHANGE_SUBSCRIPTION_PLAN]>
  updatePaymentSource: (
    args: Parameters<OwnerDetailModuleActions[OwnerDetailActions.UPDATE_PAYMENT_SOURCE]>[1]
  ) => ReturnType<OwnerDetailModuleActions[OwnerDetailActions.UPDATE_PAYMENT_SOURCE]>
  cancelSubscriptionPlan: (
    args: Parameters<OwnerDetailModuleActions[OwnerDetailActions.CANCEL_SUBSCRIPTION_PLAN]>[1]
  ) => ReturnType<OwnerDetailModuleActions[OwnerDetailActions.CANCEL_SUBSCRIPTION_PLAN]>
  revertSubscriptionCancellation: (
    args: Parameters<
      OwnerDetailModuleActions[OwnerDetailActions.REVERT_SUBSCRIPTION_CANCELLATION]
    >[1]
  ) => ReturnType<OwnerDetailModuleActions[OwnerDetailActions.REVERT_SUBSCRIPTION_CANCELLATION]>
  getUpgradePlanInfo: (
    args: Parameters<OwnerDetailModuleActions[OwnerDetailActions.GET_UPGRADE_PLAN_INFO]>[1]
  ) => ReturnType<OwnerDetailModuleActions[OwnerDetailActions.GET_UPGRADE_PLAN_INFO]>
}

export default defineComponent({
  name: 'OwnerBillingMixin',

  mixins: [OwnerDetailMixin, ContextMixin],

  data() {
    return {
      MODE: {
        MONTHLY: 'monthly',
        ANNUAL: 'annual'
      }
    }
  },

  computed: {
    ...(mapState<OwnerDetailModuleState>('owner/detail', {
      billingInfo: (state: OwnerDetailModuleState) => state.billingInfo
    }) as {
      billingInfo: () => GetBillingInfoPayload
    }),

    ownerBillingInfo(): BillingInfo {
      return (this.owner.billingInfo ?? {}) as BillingInfo
    },

    billingBackendHandler(): string {
      const billingHandlerMap = {
        [BillingBackend.st]: 'Stripe',
        [BillingBackend.mb]: 'Manual billing',
        [BillingBackend.gh]: 'GitHub'
      }

      if (Object.keys(this.ownerBillingInfo).length)
        return billingHandlerMap[this.ownerBillingInfo.billingBackend as BillingBackend]

      return ''
    },

    currentPlan(): Record<string, string | number> {
      if (Object.keys(this.ownerBillingInfo).length) {
        const { planSlug } = this.ownerBillingInfo
        if (planSlug) {
          const planInfo = this.context.plans[planSlug]

          // Show `Enterprise` as plan for cloud customers on manual billing
          // plan-premium-monthly/plan-premium-annual
          if (this.isBilledManually && planSlug.includes('premium')) {
            return { ...planInfo, name: 'Enterprise' }
          }

          return planInfo ?? {}
        }
      }

      return {}
    },

    isBilledByStripe(): boolean {
      return this.ownerBillingInfo.billingBackend === BillingBackend.st
    },

    isBilledByGitHub(): boolean {
      return this.ownerBillingInfo.billingBackend === BillingBackend.gh
    },

    isBilledManually(): boolean {
      return this.ownerBillingInfo.billingBackend === BillingBackend.mb
    },

    isBillingScaPending(): boolean {
      return this.ownerBillingInfo.status === SubscriptionStatusChoice.ScaRequired
    },

    isBillingPastDue(): boolean {
      return this.ownerBillingInfo.status === SubscriptionStatusChoice.PastDue
    }
  },

  methods: {
    ...(mapActions('owner/detail', {
      fetchBillingDetails: OwnerDetailActions.FETCH_BILLING_DETAILS,
      fetchSeatsInfo: OwnerDetailActions.FETCH_SEATS_INFO,
      fetchBillingStatus: OwnerDetailActions.FETCH_BILLING_STATUS,
      applyCredits: OwnerDetailActions.APPLY_CREDITS,
      updateBillingInfo: OwnerDetailActions.UPDATE_BILLING_INFO,
      getBillingInfo: OwnerDetailActions.GET_BILLING_INFO,
      subscriptionCheckout: OwnerDetailActions.CHECKOUT,
      updateSeats: OwnerDetailActions.UPDATE_SEATS,
      changeSubscriptionPlan: OwnerDetailActions.CHANGE_SUBSCRIPTION_PLAN,
      updatePaymentSource: OwnerDetailActions.UPDATE_PAYMENT_SOURCE,
      cancelSubscriptionPlan: OwnerDetailActions.CANCEL_SUBSCRIPTION_PLAN,
      revertSubscriptionCancellation: OwnerDetailActions.REVERT_SUBSCRIPTION_CANCELLATION,
      getUpgradePlanInfo: OwnerDetailActions.GET_UPGRADE_PLAN_INFO
    }) as OwnerDetailStoreActions)
  }
})
